import React, { forwardRef, useEffect, useMemo, useState, useRef } from "react";
import "./index.styles.scss";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { Spinner } from "react-bootstrap";
import { useAppDispatch, useAppSelector } from "Redux/hooks";
import { fetchSteelTypes } from "Redux/middlwares/steelType";
import checkIcon from "../../assets/img/icon/check.svg";
import { useTranslation } from "react-i18next";
import PreviewRebarTable from "./rebars";
import PdfOrderSummary from "./summary";
import PreviewStandardNets from "./nets/StandardNet";
import PreviewSpecialNets from "./nets/SpecialNet";
import PreviewPileTable from "./pile";
import toast from "react-hot-toast";

const PdfPreview = forwardRef(
  (
    {
      setDownloadFlag,
      downloadFlag,
      setGeneratePdfFlag,
      generatePdfFlag,
      setPdfFile,
      pdfFile,
      printCanvas,
      setPrintCanvas,
      setLoader,
      loader,
      setOrderPdfDownload,
      orderPdfDownload,
      onHide,
      order,
      rebars,
      nets,
      piles,
      headers,
      priceShow,
      setPdfType,
      pdfType,
      bidFees,
      pdfLanguage,
      feeType,
    }: any,
    ref
  ) => {
    const pageSize = 1400;
    const { i18n, t } = useTranslation(["common", "warning"]);
    const dispatch = useAppDispatch();
    const rightToLeft = useAppSelector((state) => state.root.rightToLeft);
    const steelTypes = useAppSelector((state) => state.steelType.steelTypes);

    const stylingRef = useRef();
    const documentExportBusy = useRef(false);

    const [rebarsRefs, setRebarsRef] = useState();
    const [standardNetRefs, setStandardNetsRefs] = useState();
    const [specialNetsRef, setSpecialNetsRef] = useState();
    const [pilesRef, setPilesRef] = useState();
    const [summaryRef, setSummaryRef] = useState();
    const [standardNetTran, setStandardNetTran] = useState({
      units: 0,
      weight: 0,
    });

    const [rebarsTotalPrice, setRebarsTotalPrice] = useState(0);
    const [standardNetsTotalPrice, setStandardNetsTotalPrice] = useState(0);
    const [specialNetsTotalPrice, setSpecialNetsTotalPrice] = useState(0);
    const [customRightToLeft, setCustomRightToLeft] = useState(rightToLeft);

    const totalPages = useMemo(() => {
      let total = {};
      if (rebarsRefs && (rebarsRefs as any).current)
        total = {
          rebars: (rebarsRefs as any).current.length,
        };

      if (standardNetRefs && (standardNetRefs as any).current)
        total = {
          ...total,
          standardNets: (standardNetRefs as any).current.length,
        };

      if (specialNetsRef && (specialNetsRef as any).current)
        total = {
          ...total,
          specialNets: (specialNetsRef as any).current.length,
        };

      if (pilesRef && (pilesRef as any).current)
        total = {
          ...total,
          piles: (pilesRef as any).current.length,
        };

        if (summaryRef && (summaryRef as any).current) {
        total = { 
          ...total,
          summary: (summaryRef as any).current.length,
        };
      }

      return total;
    }, [rebarsRefs, standardNetRefs, specialNetsRef, pilesRef, summaryRef]);

    //For Admin Panel Pricing Calculator Pdfs
    useEffect(() => {
      if (pdfLanguage === "he") {
        i18n.changeLanguage("he");
        stylingRef.current.setAttribute("dir", "rtl");
        stylingRef.current.setAttribute("style", "text-align: right");
        setCustomRightToLeft(true);
      } else if (pdfLanguage === "en") {
        i18n.changeLanguage("en");
        setCustomRightToLeft(false);
      }
    }, [pdfLanguage]);

    useEffect(() => {
      steelTypes.length === 0 && dispatch(fetchSteelTypes());
    }, [steelTypes, dispatch]);

    useEffect(() => {
      const hasElements = (ref) => ref?.current?.length > 0;

      const refsHaveElements =
        hasElements(rebarsRefs) ||
        hasElements(standardNetRefs) ||
        hasElements(specialNetsRef) ||
        hasElements(pilesRef) ||
        hasElements(summaryRef);

      if (refsHaveElements) {
        if (downloadFlag || orderPdfDownload) {
          setLoader(true);
          print(true);
        } else if (generatePdfFlag) {
          setLoader(true);
          setTimeout(() => {
            print(false);
          }, 500);
        } else if (printCanvas) {
          setLoader(true);
          setTimeout(() => {
            openPrintCanvas();
          }, 500);
        }
      }
    }, [
      rebarsRefs,
      standardNetRefs,
      specialNetsRef,
      pilesRef,
      summaryRef,
      generatePdfFlag,
      downloadFlag,
      printCanvas,
    ]);

    // helper to convert html to canvas
   const waitForImages = async (element: HTMLElement): Promise<void> => {
  const images = Array.from(element.querySelectorAll("img"));

  await Promise.all(
  images.map(async (img) => {
    if (img.complete && img.naturalWidth > 0) {
      try {
        await img.decode();
      } catch {}

      return;
    }

    await new Promise<void>((resolve) => {
      let finished = false;

      const finish = () => {
        if (finished) return;
        finished = true;
        resolve();
      };

      img.addEventListener("load", finish, { once: true });
      img.addEventListener("error", finish, { once: true });

      window.setTimeout(finish, 15000);
    });

    try {
      await img.decode();
    } catch {}

    return;
  })
);
};

// helper to convert html to canvas
const getHtmlToCanvas = async (
  ref: HTMLElement
): Promise<HTMLCanvasElement> => {
  await waitForImages(ref);

  return html2canvas(ref, {
    allowTaint: false,
    useCORS: true,
    logging: true,
    backgroundColor: "#ffffff",
    imageTimeout: 15000,
  });
};
    function openPrintCanvas() {
      const canvases = generateCanvases();
      let windowContent = "";
      let dataUrl;

      canvases
        .then((values) => {
          values.forEach((canvas: any) => {
            dataUrl = canvas.toDataURL();
            windowContent += "<div style={break-after:page}>";
            windowContent += `<img src=${dataUrl} style="width: 100%; height: auto"/>`;
            windowContent += "</div>";
          });
          const printWin = window.open(
            "https://easybar.co.il/new-order",
            "Easybar.co.il",
            "top=40,left=40,height=700,width=1000,focused=false,resizable=no|0,scrollbars=no,location=no|0"
          );

          if (printWin) {
            // @ts-ignore: Object is possibly 'null'.
            printWin.document.open();
            // @ts-ignore: Object is possibly 'null'.
            printWin.document.write(`
              <!DOCTYPE html>
              <html>
                <head>
                  <meta name="viewport" content="width=device-width, initial-scale=1.0">
                  <title></title>
                </head>
                <body style="width:100%;margin:0 auto;">
                ${windowContent}
                <script>
                  function triggerPrint(event) {
                    window.removeEventListener('load', triggerPrint, false);
                    setTimeout(function() {
                    closeWindow(window.print());
                    }, 100);
                  }
                  function closeWindow(){
                    window.close();
                  }
                  window.addEventListener('load', triggerPrint, false);
                </script>
                </body>
              </html>
          `);
            // @ts-ignore: Object is possibly 'null'.
            printWin.document.close();
          } else {
            toast.error(t("Unable to open print window."));
          }
          setLoader(false);
          onHide();
          setPrintCanvas();
        })
        .catch((err) => {
          toast.error(t("Error opening print preview."));
          console.error("Print Canvas Error:", err);
        });
    }

    function getDocumentPages(): HTMLElement[] {
      return [rebarsRefs, standardNetRefs, specialNetsRef, pilesRef, summaryRef]
        .flatMap((ref: any) => (ref?.current || []).filter(Boolean));
    }

    async function generateCanvases() {
      const canvases: HTMLCanvasElement[] = [];
      for (const page of getDocumentPages()) {
        canvases.push(await getHtmlToCanvas(page));
      }
      return canvases;
    }

    async function print(checkIfDownload: boolean) {
      if (documentExportBusy.current) return;
      documentExportBusy.current = true;
      try {
        document.body.classList.add("not-click");
        setLoader(true);

        const pages = getDocumentPages();
        const pdf = new jsPDF("p", "pt", "a4", true);
        // Render sequentially: parallel DOM clones exhaust memory on long orders.
        for (let index = 0; index < pages.length; index++) {
          const canvas = await getHtmlToCanvas(pages[index]);
          const imgData = canvas.toDataURL("image/png");
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
          pdf.addImage(imgData, "PNG", 0, 1, pdfWidth, pdfHeight, "", "FAST");
          canvas.width = 0;
          canvas.height = 0;
          if (index < pages.length - 1) pdf.addPage();
        }

        if (checkIfDownload) {
          pdf.save(`easybar_${order.order_no}.pdf`);
          onHide();

          if (downloadFlag) {
            setDownloadFlag();
          } else if (orderPdfDownload) {
            setOrderPdfDownload();
          }

          toast(t("download alert", { ns: "warning" }), {
            icon: <img src={checkIcon} alt="alert icon" />,
          });
        } else {
          const blob = pdf.output("blob");
          setPdfFile(blob);
          setGeneratePdfFlag();
        }
      } catch (error) {
        toast.error(t("Error during PDF generation. Please try again."));
        console.error("Error generating PDF:", error);
      } finally {
        documentExportBusy.current = false;
        document.body.classList.remove("not-click");
        setLoader(false);
      }
    }

    // handlers
    const handleRebarsRefs = (refs: any) => {
      setRebarsRef((previous: any) =>
        previous?.current?.length === refs.current.length &&
        previous.current.every((node: any, index: number) => node === refs.current[index])
          ? previous : { current: [...refs.current] });
    };
    const handleStandardNetRefs = (refs: any) => {
      setStandardNetsRefs((previous: any) =>
        previous?.current?.length === refs.current.length &&
        previous.current.every((node: any, index: number) => node === refs.current[index])
          ? previous : { current: [...refs.current] });
    };
    const handleSpecialdNetRefs = (refs: any) => {
      setSpecialNetsRef((previous: any) =>
        previous?.current?.length === refs.current.length &&
        previous.current.every((node: any, index: number) => node === refs.current[index])
          ? previous : { current: [...refs.current] });
    };
    const hanldePileRefs = (refs: any) => {
      setPilesRef((previous: any) =>
        previous?.current?.length === refs.current.length &&
        previous.current.every((node: any, index: number) => node === refs.current[index])
          ? previous : { current: [...refs.current] });
    };

    const handleSummaryRefs = (refs: any) => {
  setSummaryRef((previous: any) =>
        previous?.current?.length === refs.current.length &&
        previous.current.every((node: any, index: number) => node === refs.current[index])
          ? previous : { current: [...refs.current] });
};

    // Filters
const orderedNets = useMemo(() => {
  return [...(nets || [])].sort(
    (firstNet: any, secondNet: any) =>
      (firstNet.row_number || 0) -
      (secondNet.row_number || 0)
  );
}, [nets]);

const standardNets = useMemo(
  () =>
    orderedNets.filter(
      (item: any) =>
        item.tool.standard_net
    ),
  [orderedNets]
);

const SpecialNets = useMemo(
  () =>
    orderedNets.filter(
      (item: any) =>
        !item.tool.standard_net
    ),
  [orderedNets]
);

const netPdfHeaders = useMemo(() => {
  const result: any = {
    standard: [],
    special: [],
  };

  const combinedRows: any[] =
    orderedNets.map((net: any) => ({
      rowType: "item",
      item: net,
    }));

  [...(headers?.net || [])]
    .sort(
      (
        firstHeader: any,
        secondHeader: any
      ) =>
        firstHeader.position -
        secondHeader.position
    )
    .forEach((header: any) => {
      const position = Math.max(
        0,
        Math.min(
          header.position,
          combinedRows.length
        )
      );

      combinedRows.splice(position, 0, {
        ...header,
        rowType: "header",
      });
    });

  let standardPosition = 0;
  let specialPosition = 0;
  let previousItem: any = null;

  combinedRows.forEach(
    (row: any, rowIndex: number) => {
      if (row.rowType === "item") {
        previousItem = row.item;

        if (row.item.tool.standard_net) {
          standardPosition += 1;
        } else {
          specialPosition += 1;
        }

        return;
      }

      const nextItemRow =
        combinedRows
          .slice(rowIndex + 1)
          .find(
            (nextRow: any) =>
              nextRow.rowType === "item"
          );

      const associatedItem =
        nextItemRow?.item ||
        previousItem;

      if (!associatedItem) {
        return;
      }

      if (
        associatedItem.tool.standard_net
      ) {
        result.standard.push({
          ...row,
          position: standardPosition,
        });

        standardPosition += 1;
      } else {
        result.special.push({
          ...row,
          position: specialPosition,
        });

        specialPosition += 1;
      }
    }
  );

  return result;
}, [
  orderedNets,
  headers?.net,
]);

    const summaryDetails = {
      rebars: rebars.reduce((sum: any, curr: any) => sum + curr.quantity, 0),
      rebars_weight:
        rebars.reduce(
          (sum: any, i: any) => sum + i.tool.unit_weight * i.quantity,
          0.0
        ) / 1000,
      standard_nets: standardNets.reduce(
        (sum: any, curr: any) => sum + curr.quantity,
        0
      ),
      standard_nets_weight:
        standardNets.reduce(
          (sum: any, i: any) => sum + i.tool.unit_weight * i.quantity,
          0.0
        ) / 1000,
      special_nets: SpecialNets.reduce(
        (sum: any, curr: any) => sum + curr.quantity,
        0
      ),
      special_nets_weight:
        SpecialNets.reduce(
          (sum: any, i: any) => sum + i.tool.unit_weight * i.quantity,
          0.0
        ) / 1000,
      piles: piles.reduce((sum: any, curr: any) => sum + curr.quantity, 0),
      piles_weight:
        piles.reduce(
          (sum: any, i: any) => sum + i.tool.unit_weight * i.quantity,
          0.0
        ) / 1000,
    };

    const rebarPricing = useMemo(
      () =>
        rebars.length > 0
          ? rebars
              .reduce(
                (pre: any, curr: any) => pre + curr.unit_price * curr.quantity,
                0
              )
              .toFixed(2)
          : "0",
      [rebars]
    );

    const calculatePrice = (
      price: number,
      quantity: number,
      isTotalPrice = false,
      isRebars = false,
      isStanNet = false,
      isSpecNet = false
    ) => {
      let basePrice = Number(price);

      const stdFee = (basePrice * Number(bidFees.stdVal)) / 100;
      let easyBarFee = 0;
      //check for the EasyBar FeeType
      if (feeType === 0) {
        easyBarFee =
          ((Number(basePrice) + Number(stdFee)) * Number(bidFees.easyBarFee)) /
          100;
      } else {
        //To Check if we are calculating the easyBar fee of 1 unit or not
        if (quantity === -1) {
          easyBarFee = Number(bidFees.easyBarFee);
        } else {
          easyBarFee = Number(bidFees.easyBarFee) / Number(quantity);
        }
      }

      const payFee =
        ((stdFee + easyBarFee + basePrice) * Number(bidFees.paymentFee)) / 100;

      let totalPrice = stdFee + easyBarFee + payFee + basePrice;
      //Here storing the values in state for the order summary
      if (isTotalPrice) {
        if (isRebars) {
          setRebarsTotalPrice(totalPrice);
        } else if (isStanNet) {
          setStandardNetsTotalPrice(totalPrice);
        } else {
          setSpecialNetsTotalPrice(totalPrice);
        }
      }

      return Number(totalPrice);
    };

    const checkForDecimals = (price: number) => {
      if (price <= 0.5) {
        return Number(price).toFixed(2);
      } else {
        return Number(price).toFixed(1);
      }
    };

    return (
      <>
        <button
          type="button"
          onClick={() => print(true)}
          disabled={loader}
          style={{
            padding: "6px 14px",
            fontSize: "14px",
            fontWeight: 600,
            color: "#ffffff",
            backgroundColor: "#25944f",
            border: "none",
            borderRadius: "4px",
            cursor: loader ? "not-allowed" : "pointer",
            opacity: loader ? 0.6 : 1,
            width: "auto",
            height: "auto",
            display: "inline-block",
            marginBottom: "10px",
          }}
        >
          Download PDF
        </button>
        <div
          id="PDF-Preview-main"
          style={
            loader
              ? {
                  pointerEvents: "none",
                }
              : {}
          }
          ref={stylingRef}
        >
          <PreviewRebarTable
            pageSize={pageSize}
            loader={loader}
            order={order}
            rebars={rebars}
            headers={headers?.rebar || []}
            standardNets={standardNets}
            specialNets={SpecialNets}
            handleRefs={handleRebarsRefs}
            totalPages={totalPages}
            priceShow={priceShow}
            summaryDetails={summaryDetails}
            pdfType={pdfType}
            rebarPricing={rebarPricing}
            bidFees={bidFees}
            rebarsTotalPrice={rebarsTotalPrice}
            calculatePrice={calculatePrice}
            rightToLeft={customRightToLeft}
            checkForDecimals={checkForDecimals}
          />
          <PreviewStandardNets
            pageSize={pageSize}
            order={order}
            nets={standardNets}
            headers={netPdfHeaders.standard}
            handleRefs={handleStandardNetRefs}
            totalPages={totalPages}
            priceShow={priceShow}
            setStandardNetTran={setStandardNetTran}
            summaryDetails={summaryDetails}
            pdfType={pdfType}
            bidFees={bidFees}
            rebarsTotalPrice={rebarsTotalPrice}
            standardNetsTotalPrice={standardNetsTotalPrice}
            calculatePrice={calculatePrice}
            rightToLeft={customRightToLeft}
            checkForDecimals={checkForDecimals}
          />
          <PreviewSpecialNets
            pageSize={pageSize}
            order={order}
            nets={SpecialNets}
            headers={netPdfHeaders.special}
            standardNets={standardNets}
            handleRefs={handleSpecialdNetRefs}
            totalPages={totalPages}
            priceShow={priceShow}
            standardNetTran={standardNetTran}
            summaryDetails={summaryDetails}
            pdfType={pdfType}
            rebarPricing={rebarPricing}
            bidFees={bidFees}
            rebarsTotalPrice={rebarsTotalPrice}
            standardNetsTotalPrice={standardNetsTotalPrice}
            specialNetsTotalPrice={specialNetsTotalPrice}
            calculatePrice={calculatePrice}
            rightToLeft={customRightToLeft}
            checkForDecimals={checkForDecimals}
          />
          <PreviewPileTable
            pageSize={pageSize}
            order={order}
            piles={piles}
            headers={headers?.pile || []}
            handleRefs={hanldePileRefs}
            totalPages={totalPages}
            priceShow={priceShow}
            summaryDetails={summaryDetails}
            pdfType={pdfType}
            rebarsTotalPrice={rebarsTotalPrice}
            standardNetsTotalPrice={standardNetsTotalPrice}
            specialNetsTotalPrice={specialNetsTotalPrice}
            rightToLeft={customRightToLeft}
            checkForDecimals={checkForDecimals}
          />
          <PdfOrderSummary
            pageSize={pageSize}
            order={order}
            rebars={rebars}
            nets={nets}
            piles={piles}
            handleRefs={handleSummaryRefs}
            totalPages={totalPages}
            rightToLeft={customRightToLeft}
          />
        </div>
        {loader && (
          <div className="overloaded-wrapper">
            <div className="overloaded-spinner">
              <Spinner animation="border" role="status">
                <span className="visually-hidden"></span>
              </Spinner>
              <p>Generating PDF</p>
            </div>
          </div>
        )}
      </>
    );
  }
);

export default PdfPreview;
